import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const GuidelinesStep = ({ guidelinesAcknowledged, setGuidelinesAcknowledged, isMobile }) => {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col w-full h-full bg-white rounded-lg font-sans">
      {/* Header */}
      <div className="flex flex-col justify-center items-center px-3 sm:px-4 border-b border-gray-200 shrink-0 py-2">
        <div className="flex items-center">
          <Info size={20} className="text-indigo-500 mr-2" />
          <h2 className="text-sm font-semibold text-gray-800">
            Interview Guidelines
          </h2>
        </div>

        <p className="text-gray-600 text-xs">Scroll to read and accept</p>
      </div>

      {/* Scrollable content area with reduced max height */}
      <div className="flex-1 overflow-y-auto space-y-3 max-h-[40vh] md:max-h-[50vh] px-3 py-2">
        {/* Don'ts Section */}
        <div className="mb-3">
          <div className="flex items-center mb-2 bg-red-50 p-2 rounded-md">
            <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-red-500 flex items-center justify-center mr-2 shrink-0">
              <span className="text-white font-bold text-[10px]">✕</span>
            </div>
            <h3 className="text-xs font-semibold text-red-700 m-0">
              Important Cautions
            </h3>
          </div>

          <ul className="pl-2 m-0 text-gray-700 text-xs leading-relaxed list-none">
            <li className="mb-2 pl-4 relative">
              <span className="absolute left-0 text-red-500">•</span>
              <strong className="text-gray-900">Don't refresh the tab</strong> during the interview.
            </li>
            <li className="mb-2 pl-4 relative">
              <span className="absolute left-0 text-red-500">•</span>
              <strong className="text-gray-900">Don't interrupt Sarah</strong> before she finishes speaking.
            </li>
            <li className="mb-2 pl-4 relative">
              <span className="absolute left-0 text-red-500">•</span>
              <strong className="text-gray-900">Don't use vague responses</strong> in your answers.
            </li>
            <li className="pl-4 relative">
              <span className="absolute left-0 text-red-500">•</span>
              <strong className="text-gray-900">Don't proceed</strong> without saying "Yes, I am ready."
            </li>
          </ul>
        </div>

        {/* Do's Section */}
        <div>
          <div className="flex items-center mb-2 bg-green-50 p-2 rounded-md">
            <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-green-500 flex items-center justify-center mr-2 shrink-0">
              <span className="text-white font-bold text-[10px]">✓</span>
            </div>
            <h3 className="text-xs font-semibold text-green-700 m-0">
              Best Practices
            </h3>
          </div>

          <ul className="pl-2 m-0 text-gray-700 text-xs leading-relaxed list-none">
            <li className="mb-2 pl-4 relative">
              <span className="absolute left-0 text-green-500">•</span>
              <strong className="text-gray-900">Stick to interview topics only.</strong>
            </li>
            <li className="mb-2 pl-4 relative">
              <span className="absolute left-0 text-green-500">•</span>
              <strong className="text-gray-900">Use Chrome browser</strong> for best compatibility.
            </li>
            <li className="mb-2 pl-4 relative">
              <span className="absolute left-0 text-green-500">•</span>
              When Sarah asks <strong className="text-gray-900">"Can we start?"</strong> reply with:
              <div className="my-1 p-1 bg-gray-50 border-l-4 border-green-500 rounded text-[10px]">
                "Yes, we can start the interview."
              </div>
            </li>
            <li className="mb-2 pl-4 relative">
              <span className="absolute left-0 text-green-500">•</span>
              <strong className="text-gray-900">Wait 3-5 seconds</strong> after responding.
            </li>
            <li className="pl-4 relative">
              <span className="absolute left-0 text-green-500">•</span>
              After each question, say:
              <div className="my-1 p-1 bg-gray-50 border-l-4 border-green-500 rounded text-[10px]">
                "Yes, I am ready."
              </div>
            </li>
          </ul>
        </div>
      </div>
      
      <div className="px-3 py-2 border-t border-gray-200 bg-white shrink-0">
        <div className="flex items-start">
          <input
            type="checkbox"
            id="guidelinesAcknowledged"
            className="mr-2 mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            checked={guidelinesAcknowledged}
            onChange={(e) => setGuidelinesAcknowledged(e.target.checked)}
          />
          <label
            htmlFor="guidelinesAcknowledged"
            className="text-[10px] sm:text-xs text-gray-700 leading-tight cursor-pointer"
          >
            I have read and understand the interview guidelines and agree to the{' '}
            <button 
              type="button"
              className="text-indigo-600 underline focus:outline-none hover:text-indigo-800"
              onClick={() => navigate('/terms-and-conditions')}
            >
              Terms and Conditions
            </button>{' '}
            and will follow them during my interview.
          </label>
        </div>
      </div>
    </div>
  );
};

export default GuidelinesStep;